// Hand-crafted P&ID mockup as inline SVG.
//
// Topology (Production Deck — Crude Oil Train, JSM-A):
//   Wellhead Manifold (south)
//     → SDV-101 → V-201 First-stage Separator (vertical 3-phase)
//                    │ gas top → V-301 KO Drum → Flare
//                    │ water bot → WI manifold
//                    │ oil mid → Heater H-301 → V-202 Test Separator
//                                                  → Booster Pump P-101 A/B
//                                                  → PV-301 Pressure Control
//                                                  → Export Meter Run
//                                                  → Export Riser to FPF-003 FPSO
//
// Clickable hotspots emit (type, refId) → caller routes to detail screens.

import { memo } from "react";
import type { CMLStatus } from "./types";

const INK = "#0B141E";
const INK_MID = "#4A5567";
const PRUSSIAN = "#0E4675";
const PRUSSIAN_DEEP = "#0B3454";
const AMBER = "#B07E1F";
const PAPER = "#FBFAF5";

const STATUS_FILL: Record<CMLStatus, string> = {
  ok: "#1E6F4D",
  watch: "#B45309",
  warn: "#C2410C",
  crit: "#9F1239",
  retired: "#6E7585",
};
const NEUTRAL_STATUS = "#9CA3AF";   // no-data — NEVER green (honesty: no false safety)
function statusColor(s: CMLStatus | "none"): string {
  return s === "none" ? NEUTRAL_STATUS : (STATUS_FILL[s] ?? NEUTRAL_STATUS);
}
// xzynexa worst_cml_status → a colourable status; "" / unknown → "none".
function resolveStatus(w: string | undefined): CMLStatus | "none" {
  return (w === "crit" || w === "warn" || w === "watch" || w === "ok") ? w : "none";
}
// P&ID line hotspot id → asset tag (lower-cased), to look up real status.
const LINE_TAG_PID: Record<string, string> = {
  "ln-001": "6-p-1234", "ln-002": "8-g-2001", "ln-003": "4-wi-3045",
  // ln-004 recycle + ln-005 oil feed are two drawn segments of the SAME asset.
  "ln-004": "6-p-1235", "ln-005": "6-p-1235",
};
// Equipment as professional line-art with a status-coloured BORDER (the
// integrity overlay on a real P&ID): a near-white shell + a bold outline in the
// health colour, so status reads from the border + the pill — not a painted-in
// fill. The outline uses the one shared status palette (statusColor) so a red
// vessel is the same red as a red line and the pill; no-data → prussian.
function statusStroke(s: CMLStatus | "none" | undefined): string {
  return (!s || s === "none") ? PRUSSIAN_DEEP : statusColor(s);
}
function statusSw(s: CMLStatus | "none" | undefined): number {
  return (s === "crit" || s === "warn") ? 3 : (s === "ok" || s === "watch") ? 2.2 : 1.4;
}
// Only a FAINT wash on the shell — barely there, so the symbol stays a white
// drawing with a hint of health. no-data is neutral grey-white, NEVER green
// (honesty). Undefined (standalone use) → plain paper.
const STATUS_WASH: Record<CMLStatus | "none", string> = {
  crit: "#FBEEEC", warn: "#FDF4E8", watch: "#FDF4E8", ok: "#EDF6F0",
  retired: "#F4F3EF", none: "#F4F3EF",
};
function statusFill(s: CMLStatus | "none" | undefined): string {
  return s ? STATUS_WASH[s] : PAPER;
}
// The text channel of status (colour + icon + text) — a small pill beside each
// asset/line so health reads without relying on colour alone.
const STATUS_LABEL: Record<CMLStatus | "none", string> = {
  crit: "Critical", warn: "Warn", watch: "Watch", ok: "OK", retired: "Retired", none: "No data",
};
function StatusPill({ cx, y, status, anchor = "middle" }: {
  cx: number; y: number; status?: CMLStatus | "none"; anchor?: "middle" | "start";
}): JSX.Element | null {
  if (!status || status === "none") return null;
  const label = STATUS_LABEL[status].toUpperCase();
  const w = label.length * 5.4 + 10;
  const left = anchor === "start" ? cx : cx - w / 2;
  return (
    <g pointerEvents="none">
      <rect x={left} y={y} width={w} height={13} rx={1.5} fill={statusColor(status)} />
      <text x={left + w / 2} y={y + 9.3} fontSize="8" fontWeight={700} fill="#fff" textAnchor="middle" letterSpacing="0.04em">{label}</text>
    </g>
  );
}

// ── Flow-direction chevron on a pipe run. Colour matches the line so it reads
//    with the health accent. ──────────────────────────────────────────────
function FlowArrow({ x, y, dir, color = PRUSSIAN }: {
  x: number; y: number; dir: "right" | "left" | "up" | "down"; color?: string;
}): JSX.Element {
  const s = 6;
  const pts = {
    right: `${x - s},${y - s} ${x - s},${y + s} ${x + s},${y}`,
    left: `${x + s},${y - s} ${x + s},${y + s} ${x - s},${y}`,
    up: `${x - s},${y + s} ${x + s},${y + s} ${x},${y - s}`,
    down: `${x - s},${y - s} ${x + s},${y - s} ${x},${y + s}`,
  }[dir];
  return <polygon points={pts} fill={color} pointerEvents="none" />;
}

// ── ISA instrument bubble (field-mounted = plain circle; shared display =
//    circle with a centre line) on a dashed signal lead to its tap. ─────────
function Instrument({ x, y, fn, loop, tapX, tapY, shared }: {
  x: number; y: number; fn: string; loop: string; tapX: number; tapY: number; shared?: boolean;
}): JSX.Element {
  const r = 15;
  return (
    <g pointerEvents="none" fontFamily="IBM Plex Mono, monospace">
      <path d={`M ${tapX} ${tapY} L ${x} ${y}`} stroke={PRUSSIAN} strokeWidth="1" fill="none" />
      <circle cx={x} cy={y} r={r} fill={PAPER} stroke={PRUSSIAN} strokeWidth="1.2" />
      {shared && <line x1={x - r} y1={y} x2={x + r} y2={y} stroke={PRUSSIAN} strokeWidth="0.8" />}
      <text x={x} y={y - 2} fontSize="8.5" fill={INK} textAnchor="middle" fontWeight={600} letterSpacing="0.02em">{fn}</text>
      <text x={x} y={y + 8.5} fontSize="8.5" fill={INK} textAnchor="middle">{loop}</text>
    </g>
  );
}

export type PidHotspotType = "line" | "vessel" | "instrument" | "pump";

export interface PidHotspot {
  id: string;       // ln-001 / v-201 / pt-101 ...
  type: PidHotspotType;
  label: string;
  // Click-target rect (in svg coords)
  x: number;
  y: number;
  w: number;
  h: number;
  status?: CMLStatus;
}

interface Props {
  onHotspotClick?: (h: PidHotspot) => void;
  onHotspotHover?: (h: PidHotspot | null) => void;
  hoveredId?: string | null;
  /** Real asset health keyed by lower-cased tag (worst_cml_status). Drives the
   *  live status accents; missing/"" → neutral (never green). */
  statusByTag?: Record<string, string>;
}

// All coordinates are in this fixed SVG space — picked so the schematic
// reads at A1-landscape proportions even when scaled down to fit a panel.
const VB_W = 1600;
const VB_H = 900;

const HOTSPOTS: PidHotspot[] = [
  // Vessels
  { id: "v-201", type: "vessel", label: "V-201 / 1st-stage Separator", x: 540, y: 230, w: 120, h: 320, status: "warn" },
  { id: "v-202", type: "vessel", label: "V-202 / Test Separator",      x: 900, y: 510, w: 110, h: 200, status: "ok" },
  { id: "v-301", type: "vessel", label: "V-301 / Flare KO Drum",       x: 1140, y: 110, w: 100, h: 170, status: "watch" },
  { id: "h-301", type: "vessel", label: "H-301 / Crude Heater",        x: 760, y: 460, w: 80, h: 100, status: "ok" },
  { id: "p-101a", type: "pump",  label: "P-101 A / Booster Pump",      x: 1060, y: 640, w: 70, h: 60, status: "ok" },
  { id: "p-101b", type: "pump",  label: "P-101 B / Booster Pump",      x: 1060, y: 720, w: 70, h: 60, status: "ok" },
  // Lines — rects sized to cover each line's status pill + tag + drawn run, so
  // the natural hover targets (the labels) all register.
  { id: "ln-002", type: "line", label: '8"-G-2001 / Sour Gas to Flare',     x: 600, y: 118, w: 560, h: 44, status: "watch" },
  { id: "ln-001", type: "line", label: '6"-P-1234 / Crude Oil to FPF-003',  x: 1120, y: 642, w: 390, h: 36, status: "warn" },
  { id: "ln-003", type: "line", label: '4"-WI-3045 / Water Injection',      x: 265, y: 585, w: 350, h: 36, status: "ok" },
  { id: "ln-004", type: "line", label: '6"-P-1235 / Crude Recycle',         x: 985, y: 515, w: 250, h: 36, status: "ok" },
  { id: "ln-005", type: "line", label: '6"-P-1235 / Crude Oil from V-202',  x: 655, y: 385, w: 500, h: 36, status: "ok" },
  // Instrument hotspots dropped: the bubbles aren't drawn, so the invisible
  // rects only created dead zones that stole hover/clicks from V-201.
];

function PidCanvasImpl({ onHotspotClick, onHotspotHover, hoveredId, statusByTag }: Props): JSX.Element {
  const eqStatus = (id: string): CMLStatus | "none" => resolveStatus(statusByTag?.[id]);
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      style={{ background: PAPER, fontFamily: "IBM Plex Mono, monospace" }}
    >
      {/* Drafting grid */}
      <defs>
        <pattern id="pid-grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke={PRUSSIAN} strokeWidth="0.3" opacity="0.07" />
        </pattern>
        <pattern id="pid-grid-coarse" width="200" height="200" patternUnits="userSpaceOnUse">
          <path d="M 200 0 L 0 0 0 200" fill="none" stroke={PRUSSIAN} strokeWidth="0.6" opacity="0.10" />
        </pattern>
      </defs>
      <rect width={VB_W} height={VB_H} fill="url(#pid-grid)" />
      <rect width={VB_W} height={VB_H} fill="url(#pid-grid-coarse)" />

      {/* Process lines (under symbols) */}
      <Lines hoveredId={hoveredId} statusByTag={statusByTag} />

      {/* Wellhead manifold (left edge) */}
      <WellheadManifold />

      {/* V-201 First-stage separator */}
      <VerticalSeparator x={540} y={230} w={120} h={320} tag="V-201" service="3-Phase Separator" mawp="4.5 MPa" hovered={hoveredId === "v-201"} status={eqStatus("v-201")} />

      {/* V-202 Test separator */}
      <VerticalSeparator x={900} y={510} w={110} h={200} tag="V-202" service="Test Separator" mawp="4.0 MPa" hovered={hoveredId === "v-202"} status={eqStatus("v-202")} />

      {/* V-301 Flare KO Drum (horizontal cylinder) */}
      <HorizontalKnockoutDrum x={1140} y={110} w={100} h={170} tag="V-301" service="Flare KO Drum" mawp="2.5 MPa" hovered={hoveredId === "v-301"} status={eqStatus("v-301")} />

      {/* H-301 Crude Heater */}
      <Heater x={1150} y={362} w={84} h={96} tag="H-301" hovered={hoveredId === "h-301"} status={eqStatus("h-301")} />

      {/* P-101 A/B Booster Pumps */}
      <Pump x={1060} y={640} w={70} h={60} tag="P-101 A" hovered={hoveredId === "p-101a"} status={eqStatus("p-101a")} />
      <Pump x={1060} y={720} w={70} h={60} tag="P-101 B" hovered={hoveredId === "p-101b"} status={eqStatus("p-101b")} />

      {/* V-201 field instruments (ISA) — pressure, temperature, level */}
      <Instrument x={724} y={258} fn="PT" loop="101" tapX={660} tapY={258} />
      <Instrument x={724} y={336} fn="TT" loop="201" tapX={660} tapY={336} />
      <Instrument x={724} y={480} fn="LT" loop="201" tapX={660} tapY={480} />

      {/* Flare arrow (top-right exit) */}
      <FlareExit x={1320} y={150} />

      {/* Riser export arrow (right edge, lower) */}
      <ExportRiser x={1500} y={670} />

      {/* Hover/click hotspots — always transparent (the symbol highlight + the
          detail card are the hover feedback; no dashed box). */}
      {HOTSPOTS.map((h) => (
        <rect
          key={h.id}
          x={h.x}
          y={h.y}
          width={h.w}
          height={h.h}
          fill="transparent"
          onClick={() => onHotspotClick?.(h)}
          onMouseEnter={() => onHotspotHover?.(h)}
          onMouseLeave={() => onHotspotHover?.(null)}
          style={{ cursor: "pointer" }}
        />
      ))}
    </svg>
  );
}

// Memoised: the host re-renders on every mouse-move (to position the hover
// card), but this static schematic only needs to repaint when hover/status
// actually change — memo keeps the SVG out of the mouse-move render path.
export const PidCanvas = memo(PidCanvasImpl);

export { HOTSPOTS as PID_HOTSPOTS };

// Resolve a hotspot to the xzynexa asset tag (lower-cased) it represents, so a
// host can look up live detail on hover. Equipment ids already ARE the tag;
// lines map through LINE_TAG_PID; instruments have no asset.
export function hotspotAssetTag(h: PidHotspot): string | null {
  if (h.type === "instrument") return null;
  return h.id.startsWith("ln-") ? (LINE_TAG_PID[h.id] ?? null) : h.id;
}

// ───────────────────────────────────────────────────────────── helpers ──

function Lines({ hoveredId, statusByTag }: { hoveredId?: string | null; statusByTag?: Record<string, string> }): JSX.Element {
  // Pipe colour = the line's real health (crit red · warn amber · ok muted green
  // · no-data neutral prussian). The line "highlights" like the equipment.
  const lineColor = (id: string): string => {
    const st = resolveStatus(statusByTag?.[LINE_TAG_PID[id] ?? ""]);
    return st === "none" ? PRUSSIAN : statusColor(st);
  };
  // Thicker pipe = worse health, so the highlight reads as boldly as the
  // equipment fill (crit/warn boldest, ok medium, no-data normal).
  const main = (id: string, base = 2.4): { stroke: string; width: number } => {
    const st = resolveStatus(statusByTag?.[LINE_TAG_PID[id] ?? ""]);
    const w = (st === "crit" || st === "warn") ? base + 1.8 : st === "ok" ? base + 0.9 : base;
    return { stroke: hoveredId === id ? AMBER : lineColor(id), width: hoveredId === id ? w + 1 : w };
  };
  const ln001 = main("ln-001");
  const ln002 = main("ln-002");
  const ln003 = main("ln-003");
  const ln004 = main("ln-004");

  return (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      {/* Wellhead inlet → V-201 */}
      <path
        d="M 280 360 H 540"
        stroke={PRUSSIAN}
        strokeWidth="2.4"
      />
      <text x={350} y={350} fontSize="11" fill={INK_MID}>10"-WH-IN-001-A1A</text>

      {/* Gas top of V-201 → V-301 KO Drum → Flare exit */}
      <path d={`M 600 230 V 145 H ${1140}`} stroke={ln002.stroke} strokeWidth={ln002.width} />
      <StatusPill cx={754} y={125} status={resolveStatus(statusByTag?.["8-g-2001"])} anchor="start" />
      <text x={780} y={135} fontSize="11" fill={INK_MID}>8"-G-2001-A1S · Sour Gas</text>
      <path d="M 1240 150 H 1312" stroke={PRUSSIAN} strokeWidth="2.4" />

      {/* Oil mid of V-201 → H-301 (under V-301) → V-202 — straight into the inlet */}
      <path d="M 660 410 H 1150" stroke={PRUSSIAN} strokeWidth="2.4" />
      <StatusPill cx={814} y={392} status={resolveStatus(statusByTag?.["6-p-1235"])} anchor="start" />
      <text x={840} y={402} fontSize="10" fill={INK_MID}>6"-P-1235-A1A-H</text>
      <path d="M 1192 458 V 484 H 955 V 510" stroke={PRUSSIAN} strokeWidth="2.4" />

      {/* V-201 water bot → WI manifold (south-west) */}
      <path d={`M 600 550 V 612 H 280`} stroke={ln003.stroke} strokeWidth={ln003.width} />
      <StatusPill cx={274} y={593} status={resolveStatus(statusByTag?.["4-wi-3045"])} anchor="start" />
      <text x={300} y={603} fontSize="11" fill={INK_MID}>4"-WI-3045-A1B · Water Inj</text>

      {/* V-202 → P-101 A/B suction (lines meet the pump-circle edge at x=1067) */}
      <path d="M 955 710 V 750 H 1067" stroke={PRUSSIAN} strokeWidth="2" />
      <path d="M 955 710 V 670 H 1067" stroke={PRUSSIAN} strokeWidth="2" />

      {/* P-101 A & B discharge → common header (x=1180) → Export Riser.
          Both pumps tie into the header at (1180,680); B is the standby. */}
      <path d="M 1123 670 H 1490" stroke={ln001.stroke} strokeWidth={ln001.width} />
      <path d="M 1123 750 H 1180 V 670" stroke={PRUSSIAN} strokeWidth="2" />
      <StatusPill cx={1152} y={648} status={resolveStatus(statusByTag?.["6-p-1234"])} anchor="start" />
      <text x={1210} y={658} fontSize="11" fill={INK_MID}>6"-P-1234-A1A-H · FPF-003 ↦</text>

      {/* Recycle 6"-P-1235 from booster header back to V-202 (small flow control loop) */}
      <path d={`M 1180 670 V 540 H 1010`} stroke={ln004.stroke} strokeWidth={ln004.width} strokeDasharray="6 4" />
      <StatusPill cx={1024} y={514} status={resolveStatus(statusByTag?.["6-p-1235"])} anchor="start" />
      <text x={1050} y={524} fontSize="10" fill={INK_MID}>6"-P-1235-A1A-H · Recycle</text>

      {/* Flow-direction chevrons on the main runs */}
      <FlowArrow x={415} y={360} dir="right" />
      <FlowArrow x={880} y={145} dir="right" color={ln002.stroke} />
      <FlowArrow x={905} y={410} dir="right" />
      <FlowArrow x={440} y={612} dir="left" color={ln003.stroke} />
      <FlowArrow x={1310} y={670} dir="right" color={ln001.stroke} />
      <FlowArrow x={1095} y={540} dir="left" color={ln004.stroke} />

      {/* Valves on main routes (drawn as small bow-ties) */}
      <ValveBow x={500} y={360} flip="h" subtype="gate" tag="SDV-101" />
      <ValveBow x={700} y={410} flip="h" subtype="globe" tag="FV-201" />
      {/* CV-101 / ESV-001 removed per request */}
      <ValveBow x={540} y={612} flip="h" subtype="ball" tag="MV-301" />
      <ValveBow x={1280} y={150} flip="h" subtype="gate" tag="FV-301" />
    </g>
  );
}

function ValveBow({
  x,
  y,
  flip = "h",
  subtype,
  tag,
}: {
  x: number;
  y: number;
  flip?: "h" | "v";
  subtype: "gate" | "globe" | "check" | "ball";
  tag?: string;
}): JSX.Element {
  const w = 16;
  const h = 12;
  const halfW = flip === "h" ? w : h;
  const halfH = flip === "h" ? h : w;
  let hat = <></>;
  if (subtype === "globe") hat = <circle cx={x} cy={y - halfH - 3} r="3.5" fill={PAPER} stroke={INK} strokeWidth="1" />;
  if (subtype === "ball") hat = <circle cx={x} cy={y - halfH - 3} r="4" fill={PAPER} stroke={INK} strokeWidth="1" />;
  if (subtype === "check") hat = <polygon points={`${x + 3},${y - 1} ${x + 11},${y - 1} ${x + 7},${y - 6}`} fill={INK} />;
  const stem =
    subtype !== "gate" ? (
      <line x1={x} y1={y} x2={x} y2={y - halfH - 6} stroke={INK} strokeWidth="0.9" />
    ) : null;
  return (
    <g>
      <polygon
        points={`${x - halfW},${y - halfH} ${x},${y} ${x - halfW},${y + halfH}`}
        fill={PAPER}
        stroke={INK}
        strokeWidth="1.1"
      />
      <polygon
        points={`${x + halfW},${y - halfH} ${x},${y} ${x + halfW},${y + halfH}`}
        fill={PAPER}
        stroke={INK}
        strokeWidth="1.1"
      />
      {stem}
      {hat}
      {tag && (
        <text x={x} y={y + halfH + 12} fontSize="9" textAnchor="middle" fill={INK} letterSpacing="0.05em">
          {tag}
        </text>
      )}
    </g>
  );
}

function WellheadManifold(): JSX.Element {
  return (
    <g>
      {/* 4 wellhead trees coming into a vertical manifold header */}
      <rect x={220} y={300} width={60} height={120} fill={PAPER} stroke={PRUSSIAN_DEEP} strokeWidth="1.4" />
      <text x={250} y={295} fontSize="10" fill={PRUSSIAN_DEEP} textAnchor="middle" letterSpacing="0.08em">
        WELLHEAD HDR
      </text>
      {[0, 1, 2, 3].map((i) => {
        const wy = 320 + i * 28;
        return (
          <g key={i}>
            <line x1={140} y1={wy} x2={220} y2={wy} stroke={PRUSSIAN} strokeWidth="1.8" />
            {/* tree symbol */}
            <circle cx={130} cy={wy} r="8" fill={PAPER} stroke={INK} strokeWidth="1" />
            <line x1={130} y1={wy - 8} x2={130} y2={wy + 8} stroke={INK} strokeWidth="0.8" />
            <line x1={122} y1={wy} x2={138} y2={wy} stroke={INK} strokeWidth="0.8" />
            <text x={106} y={wy + 3} fontSize="9" fill={INK_MID} textAnchor="end">W-{(i + 1).toString().padStart(2, "0")}</text>
          </g>
        );
      })}
    </g>
  );
}

function VerticalSeparator({
  x,
  y,
  w,
  h,
  tag,
  service,
  mawp,
  hovered,
  status,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  tag: string;
  service: string;
  mawp: string;
  hovered?: boolean;
  status?: CMLStatus | "none";
}): JSX.Element {
  const cx = x + w / 2;
  const cy = y + h / 2;
  const stroke = hovered ? AMBER : statusStroke(status);
  const sw = hovered ? 2 : statusSw(status);
  return (
    <g>
      {/* Shell — rounded top + bottom */}
      <path
        d={`M ${x} ${y + 20}
            Q ${x} ${y} ${cx} ${y}
            Q ${x + w} ${y} ${x + w} ${y + 20}
            L ${x + w} ${y + h - 20}
            Q ${x + w} ${y + h} ${cx} ${y + h}
            Q ${x} ${y + h} ${x} ${y + h - 20}
            Z`}
        fill={statusFill(status)}
        stroke={stroke}
        strokeWidth={sw}
      />
      {/* Tag — centred on the shell. The backing matches the health fill so the
          vessel reads as one colour (covers the internal lines, no paper band). */}
      <rect x={x + 6} y={cy - 15} width={w - 12} height={30} fill={statusFill(status)} />
      <text x={cx} y={cy} fontSize="12" fontWeight="700" fill={PRUSSIAN_DEEP} textAnchor="middle" letterSpacing="0.06em">
        {tag}
      </text>
      <text x={cx} y={cy + 12} fontSize="8" fill={INK_MID} textAnchor="middle" letterSpacing="0.03em">
        {service.toUpperCase()}
      </text>
      <StatusPill cx={cx} y={cy + 19} status={status} />
      {/* MAWP nameplate — to the left of the head so a top nozzle stays centred */}
      <text x={x + 4} y={y - 6} fontSize="8" fill={INK_MID} textAnchor="start" letterSpacing="0.04em">
        MAWP {mawp}
      </text>
    </g>
  );
}

function HorizontalKnockoutDrum({
  x,
  y,
  w,
  h,
  tag,
  service,
  mawp,
  hovered,
  status,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  tag: string;
  service: string;
  mawp: string;
  hovered?: boolean;
  status?: CMLStatus | "none";
}): JSX.Element {
  const stroke = hovered ? AMBER : statusStroke(status);
  const sw = hovered ? 2 : statusSw(status);
  const cy = y + h / 2;
  return (
    <g>
      <path
        d={`M ${x + 18} ${y}
            L ${x + w - 18} ${y}
            Q ${x + w} ${y} ${x + w} ${cy}
            Q ${x + w} ${y + h} ${x + w - 18} ${y + h}
            L ${x + 18} ${y + h}
            Q ${x} ${y + h} ${x} ${cy}
            Q ${x} ${y} ${x + 18} ${y}
            Z`}
        fill={statusFill(status)}
        stroke={stroke}
        strokeWidth={sw}
      />
      <text x={x + w / 2} y={cy - 4} fontSize="11" fontWeight="700" fill={PRUSSIAN_DEEP} textAnchor="middle">{tag}</text>
      <text x={x + w / 2} y={cy + 10} fontSize="8" fill={INK_MID} textAnchor="middle" letterSpacing="0.03em">
        {service.toUpperCase()}
      </text>
      <StatusPill cx={x + w / 2} y={cy + 17} status={status} />
      <text x={x + w / 2} y={y - 6} fontSize="8" fill={INK_MID} textAnchor="middle">MAWP {mawp}</text>
    </g>
  );
}

function Heater({
  x,
  y,
  w,
  h,
  tag,
  hovered,
  status,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  tag: string;
  hovered?: boolean;
  status?: CMLStatus | "none";
}): JSX.Element {
  const stroke = hovered ? AMBER : statusStroke(status);
  const sw = hovered ? 2 : statusSw(status);
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill={statusFill(status)} stroke={stroke} strokeWidth={sw} />
      <text x={x + w / 2} y={y + h / 2 - 2} fontSize="11" fontWeight="700" fill={PRUSSIAN_DEEP} textAnchor="middle">{tag}</text>
      <text x={x + w / 2} y={y + h / 2 + 14} fontSize="8" fill={INK_MID} textAnchor="middle" letterSpacing="0.04em">HEATER</text>
    </g>
  );
}

function Pump({
  x,
  y,
  w,
  h,
  tag,
  hovered,
  status,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  tag: string;
  hovered?: boolean;
  status?: CMLStatus | "none";
}): JSX.Element {
  const stroke = hovered ? AMBER : statusStroke(status);
  const sw = hovered ? 2 : statusSw(status);
  const cx = x + w / 2;
  const cy = y + h / 2;
  const r = Math.min(w, h) / 2 - 2;
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill={statusFill(status)} stroke={stroke} strokeWidth={sw} />
      {/* impeller arrow — points in the discharge direction (right: suction from
          V-202 on the left → discharge to the booster header on the right) */}
      <polygon points={`${cx + r * 0.6},${cy} ${cx - r * 0.3},${cy - r * 0.5} ${cx - r * 0.3},${cy + r * 0.5}`} fill={INK} opacity="0.85" />
      <text x={cx} y={cy + r + 13} fontSize="10" fontWeight="700" fill={PRUSSIAN_DEEP} textAnchor="middle">{tag}</text>
    </g>
  );
}

function FlareExit({ x, y }: { x: number; y: number }): JSX.Element {
  return (
    <g>
      <polygon points={`${x - 8},${y - 8} ${x - 8},${y + 8} ${x + 8},${y}`} fill={AMBER} opacity="0.85" />
      <text x={x + 14} y={y + 4} fontSize="10" fill={PRUSSIAN_DEEP} letterSpacing="0.06em">TO FLARE</text>
    </g>
  );
}

function ExportRiser({ x, y }: { x: number; y: number }): JSX.Element {
  return (
    <g>
      <polygon points={`${x - 10},${y - 8} ${x - 10},${y + 8} ${x + 6},${y}`} fill={PRUSSIAN} />
      {/* Label removed — the line tag "6\"-P-1234 · Crude Oil to FPF-003 ↦" already
          names the destination; the two used to overlap here. */}
    </g>
  );
}

// Re-export STATUS_FILL for legend use if needed
export { STATUS_FILL };
